<template>
  <div
    class="language-selector"
    @click.stop="toggleLanguageMenu"
  >
    {{ currentLanguageText }}
    <div
      v-if="showLanguageMenu"
      class="language-dropdown"
      @click.stop
    >
      <div
        class="language-item"
        :class="{ active: currentLanguage === '简' }"
        @click="selectLanguage('简')"
      >
        简体
      </div>
      <div
        class="language-item"
        :class="{ active: currentLanguage === 'EN' }"
        @click="selectLanguage('EN')"
      >
        English
      </div>
      <div
        class="language-item"
        :class="{ active: currentLanguage === '繁' }"
        @click="selectLanguage('繁')"
      >
        繁体
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { setLocale, t } from '@/utils/i18n'

export default {
  name: 'LanguageSwitcher',
  setup() {
    const showLanguageMenu = ref(false)
    
    // 初始化显示语言类型（用于区分简/繁）
    const normalizeDisplayLanguage = (lang) => {
      if (lang === '简体') return '简'
      if (lang === '繁体') return '繁'
      return lang
    }

    const getInitialDisplayLanguage = () => {
      const savedDisplayLang = localStorage.getItem('displayLanguage')
      if (savedDisplayLang) {
        return normalizeDisplayLanguage(savedDisplayLang)
      }
      const savedLocale = localStorage.getItem('locale')
      if (savedLocale === 'en') {
        return 'EN'
      } else if (savedLocale === 'zh-tw') {
        return '繁'
      }
      return '简'
    }
    
    // 存储用户选择的显示语言类型
    const displayLanguage = ref(getInitialDisplayLanguage())

    // 将 i18n 的 locale 和显示语言类型映射为当前语言
    const currentLanguage = computed(() => {
      return displayLanguage.value
    })

    const SHORT_LANGUAGE_LABELS = {
      简: '简',
      繁: '繁',
      EN: 'EN'
    }

    const currentLanguageText = computed(() => {
      return SHORT_LANGUAGE_LABELS[currentLanguage.value] || currentLanguage.value
    })

    const toggleLanguageMenu = () => {
      showLanguageMenu.value = !showLanguageMenu.value
    }

    const closeLanguageMenu = () => {
      showLanguageMenu.value = false
    }

    const selectLanguage = (lang) => {
      if (lang === currentLanguage.value) {
        closeLanguageMenu()
        return
      }

      // 更新显示语言类型
      displayLanguage.value = lang
      localStorage.setItem('displayLanguage', lang)

      // 将显示的语言类型映射为 i18n 的 locale
      let targetLocale = 'zh-cn'
      if (lang === '简') {
        targetLocale = 'zh-cn'
      } else if (lang === '繁') {
        targetLocale = 'zh-tw'
      } else if (lang === 'EN') {
        targetLocale = 'en'
      }

      // 切换 i18n 语言（Element Plus 语言会通过 ElConfigProvider 自动同步）
      setLocale(targetLocale)
      closeLanguageMenu()

      // 显示切换成功提示（用切换后的语言）
      let successMessage = t('header.langSwitchedZh')
      if (lang === '繁') {
        successMessage = t('header.langSwitchedTw')
      } else if (lang === 'EN') {
        successMessage = t('header.langSwitchedEn')
      }
      ElMessage.success(successMessage)
    }

    // 点击外部区域关闭菜单
    const handleClickOutside = (event) => {
      const languageSelector = event.target.closest('.language-selector')
      if (!languageSelector) {
        closeLanguageMenu()
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      showLanguageMenu,
      currentLanguage,
      currentLanguageText,
      toggleLanguageMenu,
      closeLanguageMenu,
      selectLanguage
    }
  }
}
</script>

<style lang="scss" scoped>
.language-selector {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 39px;
  font-size: 15px;
  color: #333333;
  cursor: pointer;
  padding: 0 10px;
  transition: color 0.3s;
  font-weight: 500;
  position: relative;
  user-select: none;
  flex-shrink: 0;

  &:hover {
    color: #0d63f3;
  }

  .language-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 120px;
    z-index: 1001;
    overflow: hidden;

    // 三角形指示器
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      right: 20px;
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid #ffffff;
    }

    .language-item {
      padding: 10px 16px;
      font-size: 14px;
      color: #000000;
      cursor: pointer;
      transition: all 0.2s;
      text-align: left;

      &:hover {
        background-color: #f5f7fa;
      }

      &.active {
        background-color: #f4f7ff;
        color: #175be5;
      }
    }
  }
}

// 移动端响应式样式
@media (max-width: 1024px) and (min-width: 769px) {
  .language-selector {
    font-size: 14px;
    padding: 0 6px;

    .language-dropdown {
      right: 0;
      min-width: 100px;
    }
  }
}

@media (max-width: 768px) {
  .language-selector {
    font-size: 14px;
    padding: 0 6px;
  }
}

@media (max-width: 480px) {
  .language-selector {
    font-size: 12px;
    padding: 0 4px;
  }
}
</style>

